import { defineMessages, useIntl } from 'react-intl';
import { AppPricingData } from 'src/contentful/product-catalog';

import Suspense from '@atlaskit/react-ufo/suspense';

import { LoadingSpinner } from '@/components/global-nav/user-profile-new/src/components/spinner';
import {
	PriceFinderMessages,
	ProductPlan,
} from '@/components/wpl/pricing/src/price-finder/constants';
import PriceFinderSection from '@/components/wpl/pricing/src/price-finder/price-finder';
import { PriceFinderContextProvider } from '@/components/wpl/pricing/src/price-finder/price-finder-context.tsx';
import { PricingContent } from '@/utils/pricing-queries/get-pricing-content-gql';
import { UserLocale } from '@/utils/useGeoCurrency';

import AtlassianIntelligence from '../expander-contents/jira/atlassian-intelligence';
import Automations from '../expander-contents/jira/automations';
import Backlog from '../expander-contents/jira/backlog';
import CrossTeamPlanning from '../expander-contents/jira/cross-team-planning';
import CustomizableApprovalProcesses from '../expander-contents/jira/customizable-approval-processes';
import ExternalCollaboration from '../expander-contents/jira/external-collaboration';
import MultiRegionDataResidency from '../expander-contents/jira/multi-region-data-residency';
import ReportsAndDashboards from '../expander-contents/jira/reports-and-dashboards';
import CommunitySupport from '../expander-contents/jira/support-free';
import PremiumSupport from '../expander-contents/jira/support-premium';
import SupportStandard from '../expander-contents/jira/support-standard';
import UnlimitedProjects from '../expander-contents/jira/unlimited-projects';
import UserRolesPermissions from '../expander-contents/jira/user-roles-permissions';

export type JiraSoftwarePricingPageProps = {
	pricing?: AppPricingData;
	countryCode?: UserLocale;
	pricingContent?: PricingContent;
	shouldAddDiscount?: boolean;
};

export const JiraPriceFinder = (props: JiraSoftwarePricingPageProps) => {
	const intl = useIntl();

	const newTagText = intl.formatMessage({
		id: 'JiraSoftwarePricingPage.priceFinder.newTagText',
		defaultMessage: 'New',
	});

	const freePlanFeatureMessages = [
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.unlimitedProjectBoards.title.v2',
					defaultMessage: 'Unlimited goals, projects, tasks, and forms',
				}),
				content: UnlimitedProjects,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.backlogAndTimeline.title',
					defaultMessage: 'Backlog, list, board, timeline, calendar, and summary views',
				}),
				content: Backlog,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.reportsAndDashboards.title',
					defaultMessage: 'Reports and dashboards',
				}),
				content: ReportsAndDashboards,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.freeAutomations.title',
					defaultMessage: '100 automation rule runs per month',
				}),
				content: Automations,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'JiraSoftwarePricingPage.priceFinder.freePlan2GBOfStorage',
				defaultMessage: '2 GB of storage',
			}),
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.freeCommunitySupport.title',
					defaultMessage: 'Support from Atlassian Community',
				}),
				content: CommunitySupport,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.features.upTo10Users.title',
				defaultMessage: 'Up to 10 users',
			}),
		},
	];

	const standardPlanFeatureMessages = [
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.userRolesPermissions.title',
					defaultMessage: 'User roles and permissions',
				}),
				content: UserRolesPermissions,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.externalCollaboration.title',
					defaultMessage: 'External collaboration',
				}),
				content: ExternalCollaboration,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.multiRegionDataResidency.title',
					defaultMessage: 'Multi-region data residency',
				}),
				content: MultiRegionDataResidency,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.standardAutomations.title',
					defaultMessage: '1,700 automation rule runs per month',
				}),
				content: Automations,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.features.standardStorage.title',
				defaultMessage: '250 GB of storage',
			}),
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.standardSupport.title',
					defaultMessage: '9/5 regional support',
				}),
				content: SupportStandard,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.features.standard5000singlesSite.title',
				defaultMessage: 'Up to 100,000 users on a single site',
			}),
		},
	];

	const premiumPlanFeatureMessages = [
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.aiPowered.title.v2',
					defaultMessage:
						'Generate, summarize, and search content with Atlassian Intelligence (AI)',
				}),
				content: AtlassianIntelligence,
			},
			lozengeText: newTagText,
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.crossTeamPlanning.title',
					defaultMessage: 'Cross-team planning and dependency management',
				}),
				content: CrossTeamPlanning,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.customizableApprovalProcesses.title',
					defaultMessage: 'Customizable approval processes',
				}),
				content: CustomizableApprovalProcesses,
			},
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.premiumAutomation.title',
					defaultMessage: 'Per user automation limits (1,000 rule runs per month)',
				}),
				content: Automations,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.features.unlimitedStorage.title',
				defaultMessage: 'Unlimited storage',
			}),
		},
		{
			featureMessage: {
				text: intl.formatMessage({
					id: 'JiraSoftwarePricingPageV2.features.premiumSupport.title',
					defaultMessage: '24/7 support for critical issues',
				}),
				content: PremiumSupport,
			},
		},
		{
			featureMessage: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.features.premiumSla.title',
				defaultMessage: '99.9% uptime SLA',
			}),
		},
	];

	const signupLimit = 100_000;

	// planNames should not be translated
	const jswProductPlans: ProductPlan[] = [
		{
			planName: 'Free',
			offeringSku: 'jira-software-free',
			description: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.priceFinder.freeDescription',
				defaultMessage: 'Free forever for 10 users',
			}),
			recommendationLimit: 0,
			signupLimit,
			signupURLSuffix: '?bundle=jira-software&edition=free',
			planFeatureMessages: freePlanFeatureMessages,
		},
		{
			planName: 'Standard',
			offeringSku: 'jira-software-standard',
			description: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.priceFinder.standardDescription',
				defaultMessage: 'Everything you need to get started',
			}),
			recommendationLimit: 10,
			signupLimit,
			signupURLSuffix: '?bundle=jira-software&edition=standard',
			planFeatureMessages: standardPlanFeatureMessages,
		},
		{
			planName: 'Premium',
			offeringSku: 'jira-software-premium',
			description: intl.formatMessage({
				id: 'JiraSoftwarePricingPageV2.priceFinder.premiumDescription',
				defaultMessage: 'Align multiple teams',
			}),
			recommendationLimit: 250,
			signupLimit,
			signupURLSuffix: '?bundle=jira-software&edition=premium',
			planFeatureMessages: premiumPlanFeatureMessages,
		},
	];

	const messages: PriceFinderMessages = defineMessages({
		headerText: {
			id: 'JiraSoftwarePricingPageV2.priceFinder.header',
			defaultMessage: '<blue>Simple, transparent pricing</blue> for every team.',
		},
		unitCountInputText: {
			id: 'JiraSoftwarePricingPage.priceFinder.unitCountInputText',
			defaultMessage: 'How many users are on your team?',
		},
		unitCountInputTextRedesign: {
			id: 'JiraSoftwarePricingPage.priceFinder.unitCountInputTextRedesign',
			defaultMessage: 'Team size:',
		},
		unitCountInputDirectBuy: {
			id: 'JiraSoftwarePricingPage.priceFinder.unitCountInputDirectBuy',
			defaultMessage: "What's your team size?",
		},
		unitCountInputTerm: {
			id: 'JiraSoftwarePricingPage.priceFinder.unitCountInputTerm',
			defaultMessage: 'users',
		},
		billingCycleText: {
			id: 'JiraSoftwarePricingPage.priceFinder.billingCycleText',
			defaultMessage: 'Billing cycle:',
		},
		unitCountInputTooltip: {
			id: 'JiraSoftwarePricingPage.priceFinder.unitCountInputTooltip',
			defaultMessage:
				'A Jira Software user is any user who can log in to Jira Software, and who exists in User Management',
		},
		cardOverSignupLimit: {
			id: 'JiraSoftwarePricingPage.priceFinder.cardOverSignupLimit',
			defaultMessage: "Your user count exceeds this plan's limit",
		},
		cardEnterpriseMinimumCount: {
			id: 'JiraSoftwarePricingPage.priceFinder.cardEnterpriseMinimumCount',
			defaultMessage:
				'Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above',
			description: 'Enterprise is a plan name and should not be translated',
		},
		cardUnitCountTierRange: {
			id: 'JiraSoftwarePricingPage.priceFinder.cardUnitCountTierRange',
			defaultMessage: 'User tier: {lowerLimit} - {upperLimit}',
		},
		cardMonthlyPerUnitCount: {
			id: 'JiraSoftwarePricingPageV2.priceFinder.cardMonthlyPerUnitCount',
			defaultMessage: 'per user / month',
		},
		cardFreeDescription: {
			id: 'JiraSoftwarePricingPage.priceFinder.cardFreeDescription',
			defaultMessage: 'Always free for 10 users',
		},
	});

	return (
		<Suspense interactionName={'jira-price-finder'} fallback={LoadingSpinner}>
			<PriceFinderContextProvider
				productPlans={jswProductPlans}
				dataProductKey="jiraSoftware"
				defaultUnitCount={150}
				shouldAddDiscount={props.shouldAddDiscount}
				ctaUrlSource="productPlans"
			>
				<PriceFinderSection
					messages={messages}
					serverSideCCPPricing={props.pricing?.ccpPricingData?.jiraSoftware}
					countryCode={props.countryCode}
				/>
			</PriceFinderContextProvider>
		</Suspense>
	);
};

JiraPriceFinder.displayName = 'JiraPriceFinder';
